import { apply, fork, put, takeLatest } from 'redux-saga/effects';

import { snackbar } from '@occc-app/design-system';
import { safeAsync } from '@occc-app/lib/safeAsync';
import { ClientError } from '@occc-app/lib/client';
import { getSingleton } from '@occc-app/Activate/api';
import {
  Audience as AudienceAPI,
  Constraint as ConstraintAPI,
  EarningMechanism as EarningMechanismAPI,
  Limit as LimitAPI,
  Offer as OfferAPI,
  OfferIncluded as OfferIncludedAPI,
  MarketingContent as MarketingContentAPI,
} from '@occc-app/Activate/apiTypes';
import { navigateTo } from '@occc-app/lib/router';

import types, { ActionCreators } from './types';
import actions from './actions';

import { saga as audiencesSaga } from '../components/Audiences/redux';
import { saga as constraintsSaga } from '../components/Constraints/redux';
import { saga as limitSaga } from '../components/Limits/redux';
import { saga as getStartedSaga } from '../components/GetStarted/redux';
import { saga as earningMechanismSaga } from '../components/EarningMechanisms/redux';
import { saga as marketingContentSaga } from '../components/MarketingContent/redux';
import { saga as metaDataSaga } from '../components/Summary/redux';

const allowedEarningMechanisms: string[] = [
  'external_fulfillment',
  'loyalty_event',
  'membership_event',
  'per_group_purchased',
  'per_item_purchased',
  'per_items_accumulated',
  'per_tagged_group_purchased',
  'per_tagged_item_purchased',
  'per_tagged_items_accumulated',
  'product_purchase',
  'total_eligible_purchase',
  'total_eligible_purchase_over_time',
  'total_spend_on_transaction',
  'total_spend_over_time',
  'variety_purchase',
  'variety_purchase_over_time',
  'visits',
];
const allowedAward: string[] = ['external_point_award', 'null', 'point_award', 'points_per'];

/* Request Offer Saga */
function* requestOffer(action: ReturnType<ActionCreators['RequestOffer']>) {
  const core = getSingleton();
  const offerData = (yield apply(core, core.offers.id(action.offerId).show, [
    {
      include: 'audiences,constraints,earning_mechanism,limits,marketing_contents',
      'fields[audiences]': 'id,audience_type',
      'fields[constraints]': 'id,constraint_type',
      'fields[earning_mechanism]': 'id,earning_mechanism_type',
      'fields[limits]': 'id,limit_type',
      'fields[marketing_contents]': 'id',
    },
  ])) as { data: OfferAPI; included: OfferIncludedAPI[] };

  if (!offerData.included) {
    offerData.included = [];
  }

  const audiences = offerData.included.filter((inc) => inc.type === 'audiences') as AudienceAPI[];
  const constraints = offerData.included.filter(
    (inc) => inc.type === 'constraints',
  ) as ConstraintAPI[];
  const limits = offerData.included.filter((inc) => inc.type === 'limits') as LimitAPI[];
  const earningMechanisms = offerData.included.filter(
    (inc) => inc.type === 'earning_mechanisms',
  ) as EarningMechanismAPI[];
  const marketingContents = offerData.included.filter(
    (inc) => inc.type === 'marketing_contents',
  ) as MarketingContentAPI[];

  const apiOfferComponents = {
    offerAttributes: offerData.data,
    audiences,
    constraints,
    limits,
    earningMechanisms,
    marketingContents,
  };
  const unsupported = earningMechanisms.find((value) => {
    const isAllowedEMType = allowedEarningMechanisms.includes(
      value.attributes.earning_mechanism_type,
    );
    const isAllowedAward = allowedAward.includes(value.attributes.award.award_type);
    return !isAllowedAward || !isAllowedEMType;
  });
  if (unsupported) {
    snackbar.warning(
      'Activate Command Center does not support editing of the Offer with this Earning Mechanism and Award.',
    );
    yield navigateTo('/offers');
    return;
  }

  yield put(actions.requestOfferOK(apiOfferComponents));
}

export const sagas = {
  requestOffer: safeAsync(requestOffer, (error: ClientError) => {
    const errorMessages = error.errorMessages || [{ message: 'Unable to request offer.' }];
    return actions.requestOfferERR(errorMessages, error);
  }),
};

export default function* saga() {
  yield takeLatest(types.REQUEST_OFFER, sagas.requestOffer);
  yield fork(audiencesSaga);
  yield fork(constraintsSaga);
  yield fork(earningMechanismSaga);
  yield fork(limitSaga);
  yield fork(getStartedSaga);
  yield fork(marketingContentSaga);
  yield fork(metaDataSaga);
}
